import { useMemo } from 'react';
import { TASKS, type Product, type TaskDefinition, type TaskType, taskOf } from '@visionqc/shared';
import { useProducts } from './useProducts';

/**
 * Which questions this installation actually asks, and what that means for the
 * navigation.
 *
 * The task belongs to a product, not to the installation, so there is no single
 * answer to "what task are we". A plant may run quality inspection on one line
 * and classification on another, and both belong in one sidebar.
 *
 * So the rule is: a route is hidden only when NO product needs it. A shop doing
 * nothing but anomaly detection never sees Classes; one doing nothing but
 * classification never sees Labels; one doing both sees both, and the pages
 * themselves narrow down per selected product.
 *
 * Hiding rather than disabling, because a control that cannot work is worse than
 * one that is absent — the operator presses it and nothing happens, and now they
 * distrust the whole screen rather than that one button.
 */
export interface TaskUsage {
  products: Product[];
  loading: boolean;
  /** Task types at least one active product uses. */
  inUse: TaskType[];
  /** Whether a nav route should be shown at all. */
  showsRoute: (path: string) => boolean;
  /** The definition for one product, for pages that render per product. */
  taskFor: (productId: number | null | undefined) => TaskDefinition;
}

export function useTasks(): TaskUsage {
  const { products, loading } = useProducts();

  return useMemo(() => {
    // Inactive products still count. A line that is off for the summer has not
    // stopped being a classification line, and hiding its pages would strand
    // the data it already collected.
    const inUse = [...new Set(products.map((p) => taskOf(p.task).id))];

    // Before anything has loaded, show everything rather than flickering items
    // in as products arrive. An empty install shows everything too: there is no
    // evidence yet for hiding, and a first-run sidebar missing half its entries
    // reads as a broken build.
    const effective: TaskType[] = inUse.length > 0 ? inUse : (Object.keys(TASKS) as TaskType[]);

    return {
      products,
      loading,
      inUse,
      showsRoute: (path: string) =>
        // Shown when at least one task in use does not hide it.
        effective.some((t) => !TASKS[t].hiddenRoutes.includes(path)),
      taskFor: (productId) => taskOf(products.find((p) => p.id === productId)?.task),
    };
  }, [products, loading]);
}
